import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { getOrders } from "../../api/adminService";
import { OrderStatusColors, OrderStatusLabels, formatCurrency, formatDate } from "./constants";

const COUPON_DISCOUNT_TYPE_LABELS = {
  1: "Percentage",
  2: "Fixed Amount",
  3: "Free Delivery",
};

export default function Orders() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  function loadOrders() {
    setLoading(true);
    getOrders({
      status: statusFilter || undefined,
      search: search || undefined,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("Could not load orders. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, paginationModel]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPaginationModel((p) => ({ ...p, page: 0 }));
    loadOrders();
  }

  const columns = useMemo(
    () => [
      { field: "orderNumber", headerName: "Order #", width: 170 },
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 160 },
      { field: "customerName", headerName: "Customer", flex: 1, minWidth: 160 },
      { field: "itemCount", headerName: "Items", width: 90 },
      {
        field: "status",
        headerName: "Status",
        width: 160,
        renderCell: (params) => (
          <Chip size="small" label={OrderStatusLabels[params.value]} color={OrderStatusColors[params.value]} />
        ),
      },
      {
        field: "couponCode",
        headerName: "Coupon",
        width: 160,
        sortable: false,
        renderCell: (params) =>
          params.row.couponCode ? (
            <Chip
              size="small"
              label={
                COUPON_DISCOUNT_TYPE_LABELS[params.row.couponDiscountType]
                  ? `${params.row.couponCode} (${COUPON_DISCOUNT_TYPE_LABELS[params.row.couponDiscountType]})`
                  : params.row.couponCode
              }
              color={params.row.couponDiscountType === 3 ? "info" : "default"}
            />
          ) : (
            "-"
          ),
      },
      {
        field: "discountAmount",
        headerName: "Discount",
        width: 110,
        align: "right",
        headerAlign: "right",
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      {
        field: "totalAmount",
        headerName: "Total",
        width: 120,
        align: "right",
        headerAlign: "right",
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "createdAt",
        headerName: "Date",
        width: 170,
        valueFormatter: (v) => formatDate(v),
      },
    ],
    []
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Orders
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack component="form" onSubmit={handleSearchSubmit} direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Search order #, customer or store"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All statuses</MenuItem>
            {Object.entries(OrderStatusLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.orderId}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          onRowClick={(params) => navigate(`/admin/orders/${params.row.orderId}`)}
          sx={{ border: "none", cursor: "pointer" }}
        />
      </Box>
    </Box>
  );
}
